import React, { useState, useEffect, useCallback, createContext, useContext, Suspense } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import axios from 'axios';
import { DebtRequirementProvider, DebtRequirementBanner } from '../../features/dealer-debt/DebtRequirement';
import { 
  Home, 
  ShoppingCart, 
  Package, 
  CreditCard, 
  FileText, 
  User, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  Check,
  CheckCheck,
  Wallet,
  ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crazy-chandrasekhar-3/artifacts/ursomwnx_Renkli-YeniMerve-Logo.png";

const DealerSidebarContext = createContext();

const menuItems = [
  { path: '/dealer', icon: Home, label: 'Ana Sayfa', exact: true },
  { path: '/dealer/shop', icon: ShoppingCart, label: 'Sipariş Ver' },
  { path: '/dealer/orders', icon: Package, label: 'Siparişlerim' },
  { path: '/dealer/payments', icon: CreditCard, label: 'Ödemeler' },
  { path: '/dealer/account', icon: FileText, label: 'Cari Hareketler' },
  { path: '/dealer/profile', icon: User, label: 'Profilim' },
];

const DealerSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const viewAsDealer = useAuthStore((s) => s.viewAsDealer);
  const { sidebarOpen, setSidebarOpen } = useContext(DealerSidebarContext);
  // View-as modunda bayi bilgilerini göster, admin oturumu arka planda devam eder
  const displayUser = (viewAsDealer && viewAsDealer.id)
    ? { name: viewAsDealer.name || viewAsDealer.company_name, company_name: viewAsDealer.company_name, email: '' }
    : user;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          <Link to="/dealer" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Merve Yayınevi" className="h-10 w-auto" />
            <div className="flex flex-col">
              <span className="font-bold text-gray-900 text-sm">Merve Yayınevi</span>
              <span className="text-xs text-gray-500">Bayi Portalı</span>
            </div>
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-gray-100 rounded"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 font-semibold text-sm">
                {(displayUser?.company_name || displayUser?.name)?.charAt(0) || 'B'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 text-sm truncate" title={displayUser?.company_name || displayUser?.name}>{displayUser?.company_name || displayUser?.name || 'Bayi'}</p>
              <p className="text-xs text-gray-500 truncate" title={displayUser?.name || displayUser?.email}>{displayUser?.company_name ? (displayUser?.name || '') : (displayUser?.email || '')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active 
                    ? 'bg-red-50 text-red-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-red-600' : 'text-gray-400'}`} />
                {item.label}
                {active && <ChevronRight className="w-4 h-4 ml-auto text-red-400" />}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 text-gray-400" />
            Çıkış Yap
          </button>
        </div>
      </aside>
    </>
  );
};

const DealerTopbar = ({ title }) => {
  const { setSidebarOpen } = useContext(DealerSidebarContext);
  const { user, token } = useAuthStore();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchCartCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/cart`, { headers: { Authorization: `Bearer ${token}` } });
        if (mounted) setCartCount(res.data?.item_count || 0);
      } catch {
        if (mounted) setCartCount(0);
      }
    };
    fetchCartCount();
    const interval = setInterval(fetchCartCount, 15000);
    return () => { mounted = false; clearInterval(interval); };
  }, [token, location.pathname]);

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      {/* Left: Menu button & Title */}
      <div className="flex items-center gap-3">
        <button
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Cart Link */}
        <Link 
          to="/dealer/cart"
          className="relative p-2 hover:bg-gray-100 rounded-lg"
          data-testid="cart-btn"
        >
          <ShoppingCart className="w-5 h-5 text-gray-600" />
          {cartCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none shadow-sm"
              data-testid="cart-count-badge"
            >
              {cartCount > 99 ? '99+' : cartCount}
            </span>
          )}
        </Link>

        {/* Notifications */}
        <NotificationBell />

        {/* User Avatar */}
        <Link to="/dealer/profile" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-red-600 font-semibold text-xs">
              {user?.name?.charAt(0) || 'B'}
            </span>
          </div>
        </Link>
      </div>
    </header>
  );
};

const NotificationBell = () => {
  const { token } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Az önce';
    if (mins < 60) return `${mins} dk önce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} saat önce`;
    const days = Math.floor(hours / 24);
    return `${days} gün önce`;
  };

  return (
    <div className="relative">
      <button onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }} className="relative p-2 hover:bg-gray-100 rounded-lg" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">Bildirimler</h3>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                  <CheckCheck className="w-3.5 h-3.5" />Tümünü Okundu Yap
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Bildirim yok</div>
              ) : (
                notifications.slice(0, 20).map(n => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${!(n.is_read || n.read) ? 'bg-red-50/40' : ''}`}
                    onClick={() => { if (!(n.is_read || n.read)) markRead(n.id); }}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!(n.is_read || n.read) && <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      {!(n.is_read || n.read) && (
                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-1 hover:bg-gray-200 rounded shrink-0" title="Okundu yap">
                          <Check className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DealerBottomNav = () => {
  const location = useLocation();

  const bottomItems = [
    { path: '/dealer', icon: Home, label: 'Ana Sayfa', exact: true },
    { path: '/dealer/shop', icon: ShoppingCart, label: 'Sipariş' },
    { path: '/dealer/orders', icon: Package, label: 'Siparişler' },
    { path: '/dealer/account', icon: Wallet, label: 'Cari' },
  ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="flex items-center justify-around h-14">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full ${
                active ? 'text-red-600' : 'text-gray-400'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export const DealerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [waSupport, setWaSupport] = useState('+90 546 469 73 43');
  const location = useLocation();
  const navigate = useNavigate();
  const viewAsDealer = useAuthStore((s) => s.viewAsDealer);
  const clearViewAsDealer = useAuthStore((s) => s.clearViewAsDealer);

  useEffect(() => {
    let ok = true;
    axios.get(`${API_URL}/api/public/brand-settings`).then((r) => {
      if (ok && r.data?.whatsapp_support_number) setWaSupport(r.data.whatsapp_support_number);
    }).catch(() => {});
    return () => { ok = false; };
  }, []);

  const openWhatsAppSupport = () => {
    // Numarayı sayilastir: sadece rakam (uluslararasi ondalik ile)
    const digits = (waSupport || '').replace(/[^\d]/g, '');
    if (!digits) return;
    const msg = encodeURIComponent('Merve Yayınevi B2B - Destek talebim var:');
    window.open(`https://wa.me/${digits}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  const handleReturnToAdmin = () => {
    // Hard navigate: React Router guard race condition'ından kaçınmak için
    // sayfayı tam yeniden yükle. clearViewAsDealer + navigate arası tick'te
    // DealerRoute guard viewAsDealer null görüp `/`'a redirect ediyor.
    clearViewAsDealer();
    window.location.href = '/admin/dealers';
  };

  // Get page title based on route
  const getTitle = () => {
    const titles = {
      '/dealer': 'Ana Sayfa',
      '/dealer/shop': 'Sipariş Ver',
      '/dealer/orders': 'Siparişlerim',
      '/dealer/payments': 'Ödemeler',
      '/dealer/debt-payment': 'Cari Borç Ödemesi',
      '/dealer/account': 'Cari Hareketler',
      '/dealer/profile': 'Profilim',
      '/dealer/cart': 'Sepetim',
    };
    return titles[location.pathname] || 'Bayi Portalı';
  };

  return (
    <DebtRequirementProvider><DealerSidebarContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className="min-h-screen bg-gray-50">
        {viewAsDealer && viewAsDealer.id && (
          <div className="sticky top-0 z-[40] text-white px-3 sm:px-4 py-2 flex items-center justify-center gap-3 shadow-md" style={{ backgroundColor: '#820303' }} data-testid="view-as-banner">
            <p className="text-xs sm:text-sm font-medium text-center">
              <span className="font-semibold">{viewAsDealer.company_name || viewAsDealer.name || 'Bayi'}</span>
              <span> hesabını görüntülüyorsunuz</span>
            </p>
            <button
              onClick={handleReturnToAdmin}
              className="flex items-center gap-1.5 px-3 py-1 rounded text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors text-white hover:opacity-90"
              style={{ backgroundColor: '#5c0202' }}
              data-testid="return-to-admin-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Admin Panele Dön</span>
            </button>
          </div>
        )}
        <DealerSidebar />
        <div className="lg:pl-64">
          <DealerTopbar title={getTitle()} />
          <main className="p-4 pb-20 lg:p-6 lg:pb-6">
            <DebtRequirementBanner/>
            <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" /></div>}><Outlet /></Suspense>
          </main>
        </div>
        <DealerBottomNav />
        {/* WhatsApp Destek FAB — v58.91 */}
        <button
          type="button"
          onClick={openWhatsAppSupport}
          className="fixed z-40 right-4 bottom-20 lg:bottom-6 flex items-center justify-center rounded-full shadow-lg hover:shadow-xl active:scale-95 transition-all"
          style={{ backgroundColor: '#25D366', width: 56, height: 56, boxShadow: '0 6px 18px rgba(37, 211, 102, 0.35)' }}
          data-testid="whatsapp-support-fab"
          title={`WhatsApp Destek · ${waSupport}`}
          aria-label="WhatsApp Destek"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="28" height="28" fill="#fff" aria-hidden="true">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.03 1.318-1.045 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.708.888.632 0 1.958-.42 2.29-1.09.2-.415.2-.775.14-.99-.13-.216-.472-.34-.99-.588z" />
            <path d="M16.002 3.2C8.936 3.2 3.2 8.936 3.2 16.002c0 2.484.72 4.802 1.955 6.756L3.2 28.8l6.256-1.906a12.687 12.687 0 0 0 6.546 1.808c7.066 0 12.802-5.736 12.802-12.802S23.068 3.2 16.002 3.2zm0 23.36c-2.184 0-4.216-.626-5.936-1.706l-.416-.245-4.06 1.234 1.288-3.94-.27-.42a10.53 10.53 0 0 1-1.65-5.673c0-5.822 4.734-10.556 10.556-10.556 5.82 0 10.554 4.734 10.554 10.556S21.822 26.56 16 26.56z" />
          </svg>
        </button>
      </div>
    </DealerSidebarContext.Provider></DebtRequirementProvider>
  );
};

export default DealerLayout;
